/**
 * Settings of one digital resource connector: portal tile, signed access link
 * (SSO), patron categories and OAI-PMH harvest. The secret is write-only.
 */

import { useId, useState, type FormEvent, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Copy, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import {
  useCheckDigitalResourceRepository,
  useHarvestDigitalResource,
  usePatronCategories,
  usePreviewDigitalResourceLink,
  useSaveDigitalResource,
} from "@/api/hooks";
import type {
  DigitalResourceSettings,
  DigitalResourcesOverview,
  RecordType,
  SignatureAlgorithm,
} from "@/api/types";
import { RECORD_TYPE_LABELS } from "@/lib/recordTypes";
import { Button } from "@/components/ui/button";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500";
const monoInputClass = `${inputClass} font-mono text-[13px]`;
const labelClass = "mb-1.5 block text-sm text-gray-700 dark:text-gray-200";
const hintClass = "mt-1 text-xs text-gray-500 dark:text-gray-400";

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });

interface DigitalResourceFormProps {
  resource: DigitalResourceSettings;
  overview: DigitalResourcesOverview;
}

export function DigitalResourceForm({ resource, overview }: DigitalResourceFormProps) {
  const id = useId();
  const save = useSaveDigitalResource(resource.id);
  const categories = usePatronCategories();

  const [name, setName] = useState(resource.name);
  const [description, setDescription] = useState(resource.description ?? "");
  const [logoUrl, setLogoUrl] = useState(resource.logo_url ?? "");
  const [active, setActive] = useState(resource.active);
  const [urlTemplate, setUrlTemplate] = useState(resource.access.url_template ?? "");
  const [algorithm, setAlgorithm] = useState<SignatureAlgorithm>(
    resource.access.signature_algorithm
  );
  const [signatureTemplate, setSignatureTemplate] = useState(
    resource.access.signature_template ?? ""
  );
  const [secret, setSecret] = useState("");
  const [libraryCode, setLibraryCode] = useState(resource.access.library_code ?? "");
  const [allowedCategories, setAllowedCategories] = useState(
    resource.access.allowed_category_codes
  );
  const [oaiUrl, setOaiUrl] = useState(resource.harvest.oai_url ?? "");
  const [oaiSet, setOaiSet] = useState(resource.harvest.oai_set ?? "");
  const [recordType, setRecordType] = useState<RecordType>(resource.harvest.record_type);
  const [saved, setSaved] = useState(false);

  const signed = algorithm !== "none";
  const personalData = overview.personal_data_placeholders.filter((placeholder) =>
    `${urlTemplate} ${signed ? signatureTemplate : ""}`.includes(`{${placeholder}}`)
  );

  // Any edit makes the "saved" confirmation stale
  const edit =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setSaved(false);
    };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        name,
        description,
        logo_url: logoUrl,
        active,
        access_url_template: urlTemplate,
        signature_algorithm: algorithm,
        signature_template: signatureTemplate,
        secret,
        library_code: libraryCode,
        allowed_category_codes: allowedCategories,
        oai_url: oaiUrl,
        oai_set: oaiSet,
        record_type: recordType,
      },
      {
        onSuccess: () => {
          setSecret("");
          setSaved(true);
        },
      }
    );
  };

  return (
    <div className="min-w-0 space-y-6">
      <form
        onSubmit={submit}
        aria-labelledby={`${id}-title`}
        className="space-y-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900"
      >
        <div>
          <h2 id={`${id}-title`} className="text-lg font-semibold text-gray-900 dark:text-white">
            {resource.provider_label}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{resource.category}</p>
        </div>

        <Section title="Sur le portail">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom affiché" htmlFor={`${id}-name`}>
              <input
                id={`${id}-name`}
                value={name}
                onChange={(e) => {
                  edit(setName)(e.target.value);
                }}
                required
                maxLength={100}
                className={inputClass}
              />
            </Field>
            <Field
              label="Logo (adresse https)"
              htmlFor={`${id}-logo`}
              hint="Fourni dans le kit de communication du fournisseur. Sans logo, une icône est affichée."
            >
              <input
                id={`${id}-logo`}
                type="url"
                value={logoUrl}
                onChange={(e) => {
                  edit(setLogoUrl)(e.target.value);
                }}
                maxLength={500}
                placeholder="https://"
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="Description" htmlFor={`${id}-description`}>
            <textarea
              id={`${id}-description`}
              value={description}
              onChange={(e) => {
                edit(setDescription)(e.target.value);
              }}
              rows={2}
              maxLength={1000}
              className={inputClass}
            />
          </Field>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => {
                edit(setActive)(e.target.checked);
              }}
              className="accent-primary mt-0.5 h-4 w-4"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">
                Ressource active
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Affichée sur le portail si l&apos;accès lecteur est configuré ; ses titres sont
                moissonnés si un entrepôt est renseigné.
              </span>
            </span>
          </label>
        </Section>

        <Section
          title="Accès lecteur (connexion directe)"
          description="Lien ouvert quand le lecteur clique sur la tuile, construit à partir de la documentation SSO du fournisseur."
        >
          <Field
            label="Modèle de lien"
            htmlFor={`${id}-url`}
            hint="Commence par https:// suivi du site du fournisseur. Les variables entre accolades sont remplacées et encodées."
          >
            <textarea
              id={`${id}-url`}
              value={urlTemplate}
              onChange={(e) => {
                edit(setUrlTemplate)(e.target.value);
              }}
              rows={2}
              maxLength={1000}
              spellCheck={false}
              placeholder="https://fournisseur.example/sso?carte={patron_id}&ts={timestamp}&cle={signature}"
              className={monoInputClass}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Signature" htmlFor={`${id}-algorithm`}>
              <select
                id={`${id}-algorithm`}
                value={algorithm}
                onChange={(e) => {
                  edit(setAlgorithm)(e.target.value as SignatureAlgorithm);
                }}
                className={inputClass}
              >
                {overview.signature_algorithms.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Identifiant de la bibliothèque"
              htmlFor={`${id}-library-code`}
              hint="Code client attribué par le fournisseur : variable {library_code}."
            >
              <input
                id={`${id}-library-code`}
                value={libraryCode}
                onChange={(e) => {
                  edit(setLibraryCode)(e.target.value);
                }}
                maxLength={100}
                autoComplete="off"
                spellCheck={false}
                className={inputClass}
              />
            </Field>
          </div>

          {signed && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Message signé"
                htmlFor={`${id}-signature`}
                hint={
                  algorithm === "hmac_sha256"
                    ? "Le secret sert de clé HMAC."
                    : "Doit contenir {secret}. Le résultat remplace {signature} dans le lien."
                }
              >
                <input
                  id={`${id}-signature`}
                  value={signatureTemplate}
                  onChange={(e) => {
                    edit(setSignatureTemplate)(e.target.value);
                  }}
                  required
                  maxLength={1000}
                  spellCheck={false}
                  placeholder="{patron_id}{timestamp}{secret}"
                  className={monoInputClass}
                />
              </Field>
              <Field
                label="Secret partagé"
                htmlFor={`${id}-secret`}
                hint={
                  resource.access.has_secret
                    ? "Enregistré et chiffré. Laissez vide pour le conserver."
                    : "Chiffré à l'enregistrement, jamais réaffiché."
                }
              >
                <input
                  id={`${id}-secret`}
                  type="password"
                  value={secret}
                  onChange={(e) => {
                    edit(setSecret)(e.target.value);
                  }}
                  required={!resource.access.has_secret && urlTemplate.trim() !== ""}
                  maxLength={256}
                  autoComplete="new-password"
                  placeholder={resource.access.has_secret ? "••••••••" : undefined}
                  className={inputClass}
                />
              </Field>
            </div>
          )}

          {personalData.length > 0 && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                Le lien transmet des données personnelles au fournisseur (
                {personalData.map((p) => `{${p}}`).join(", ")}). Vérifiez que le contrat le prévoit
                et que la politique de confidentialité du portail le mentionne.
              </span>
            </p>
          )}

          <details className="rounded-lg border border-gray-200 text-sm dark:border-gray-700">
            <summary className="cursor-pointer px-3 py-2 font-medium text-gray-700 dark:text-gray-200">
              Variables disponibles
            </summary>
            <dl className="grid gap-x-4 gap-y-1 border-t border-gray-200 px-3 py-2 sm:grid-cols-[auto_1fr] dark:border-gray-700">
              {Object.entries(overview.placeholders).map(([placeholder, meaning]) => (
                <div key={placeholder} className="contents">
                  <dt className="font-mono text-[13px] text-gray-900 dark:text-gray-100">
                    {`{${placeholder}}`}
                  </dt>
                  <dd className="text-gray-600 dark:text-gray-400">{meaning}</dd>
                </div>
              ))}
              <dt className="font-mono text-[13px] text-gray-900 dark:text-gray-100">
                {"{signature}"}
              </dt>
              <dd className="text-gray-600 dark:text-gray-400">
                Signature calculée (modèle de lien seulement)
              </dd>
              <dt className="font-mono text-[13px] text-gray-900 dark:text-gray-100">
                {"{secret}"}
              </dt>
              <dd className="text-gray-600 dark:text-gray-400">
                Secret partagé (message signé seulement, jamais dans le lien)
              </dd>
            </dl>
          </details>

          <fieldset>
            <legend className={labelClass}>Catégories de lecteurs autorisées</legend>
            {categories.isLoading ? (
              <p className={hintClass}>Chargement des catégories…</p>
            ) : categories.error ? (
              <p role="alert" className="text-sm text-red-700 dark:text-red-400">
                Impossible de charger les catégories de lecteurs.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {(categories.data ?? []).map((category) => (
                    <label key={category.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={allowedCategories.includes(category.code)}
                        onChange={(e) => {
                          edit(setAllowedCategories)(
                            e.target.checked
                              ? [...allowedCategories, category.code]
                              : allowedCategories.filter((code) => code !== category.code)
                          );
                        }}
                        className="accent-primary h-4 w-4"
                      />
                      <span className="text-gray-800 dark:text-gray-200">{category.name}</span>
                    </label>
                  ))}
                </div>
                <p className={hintClass}>
                  {allowedCategories.length === 0
                    ? "Aucune cochée : tous les lecteurs à jour de leur abonnement y ont accès."
                    : "Seuls les lecteurs de ces catégories, à jour de leur abonnement, y ont accès."}
                </p>
              </>
            )}
          </fieldset>
        </Section>

        <Section
          title="Titres dans le catalogue (OAI-PMH)"
          description="Facultatif : les titres du fournisseur sont importés dans le catalogue (à la demande ou par la tâche planifiée du serveur) et apparaissent dans la recherche du portail, avec un bouton d'accès en ligne."
        >
          <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
            <Field label="Adresse de l'entrepôt OAI-PMH" htmlFor={`${id}-oai`}>
              <input
                id={`${id}-oai`}
                type="url"
                value={oaiUrl}
                onChange={(e) => {
                  edit(setOaiUrl)(e.target.value);
                }}
                maxLength={500}
                spellCheck={false}
                placeholder="https://fournisseur.example/oai"
                className={inputClass}
              />
            </Field>
            <Field label="Lot (set)" htmlFor={`${id}-set`} hint="Facultatif.">
              <input
                id={`${id}-set`}
                value={oaiSet}
                onChange={(e) => {
                  edit(setOaiSet)(e.target.value);
                }}
                maxLength={100}
                spellCheck={false}
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="Type de document des titres" htmlFor={`${id}-record-type`}>
            <select
              id={`${id}-record-type`}
              value={recordType}
              onChange={(e) => {
                edit(setRecordType)(e.target.value as RecordType);
              }}
              className={`${inputClass} sm:max-w-xs`}
            >
              {Object.entries(RECORD_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </Section>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          {save.error && (
            <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
              {save.error.message}
            </p>
          )}
          {saved && !save.error && (
            <p role="status" className="mr-auto text-sm text-emerald-700 dark:text-emerald-400">
              Réglages enregistrés.
            </p>
          )}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" aria-hidden />}
            Enregistrer
          </Button>
        </div>
      </form>

      {resource.access.configured && <LinkPreview resource={resource} />}
      {resource.harvest.configured && <HarvestPanel resource={resource} />}
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  const id = useId();

  return (
    <section aria-labelledby={id} className="space-y-4">
      <div>
        <h3 id={id} className="text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelClass}>
        {label}
      </label>
      {children}
      {hint && <p className={hintClass}>{hint}</p>}
    </div>
  );
}

/**
 * The link the saved settings produce for a fictitious patron, to compare with
 * the provider's documentation or send to its support.
 */
function LinkPreview({ resource }: { resource: DigitalResourceSettings }) {
  const preview = usePreviewDigitalResourceLink(resource.id);
  const [copied, setCopied] = useState(false);

  return (
    <section
      aria-labelledby={`preview-${resource.id}`}
      className="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl">
          <h2
            id={`preview-${resource.id}`}
            className="text-sm font-semibold text-gray-900 dark:text-white"
          >
            Vérifier le lien
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Lien généré avec les réglages enregistrés pour un lecteur fictif (carte 00000000).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={preview.isPending}
          onClick={() => {
            setCopied(false);
            preview.mutate();
          }}
        >
          {preview.isPending && <Loader2 className="animate-spin" aria-hidden />}
          Générer un exemple
        </Button>
      </div>
      {preview.data && (
        <div className="flex items-start gap-2">
          <code className="min-w-0 flex-1 rounded-lg bg-gray-50 p-3 font-mono text-xs break-all text-gray-800 dark:bg-gray-800 dark:text-gray-200">
            {preview.data.url}
          </code>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Copier le lien"
            onClick={() => {
              void navigator.clipboard.writeText(preview.data.url).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? <CheckCircle2 aria-hidden /> : <Copy aria-hidden />}
          </Button>
        </div>
      )}
      <p role="status" className="sr-only">
        {copied ? "Lien copié." : ""}
      </p>
      {preview.error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {preview.error.message}
        </p>
      )}
    </section>
  );
}

const HARVEST_STATUS_LABELS: Record<DigitalResourceSettings["harvest"]["status"], string> = {
  never: "Jamais moissonné",
  queued: "Moissonnage en attente…",
  running: "Moissonnage en cours…",
  success: "Dernier moissonnage réussi",
  error: "Le dernier moissonnage a échoué",
};

function HarvestPanel({ resource }: { resource: DigitalResourceSettings }) {
  const check = useCheckDigitalResourceRepository(resource.id);
  const harvest = useHarvestDigitalResource(resource.id);
  const { status, message, last_harvest_at: lastHarvestAt } = resource.harvest;
  const busy = status === "queued" || status === "running";

  return (
    <section
      aria-labelledby={`harvest-${resource.id}`}
      className="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl" aria-live="polite">
          <h2
            id={`harvest-${resource.id}`}
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white"
          >
            {status === "error" && (
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden />
            )}
            {status === "success" && (
              <CheckCircle2
                className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                aria-hidden
              />
            )}
            {busy && (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
            )}
            {HARVEST_STATUS_LABELS[status]}
          </h2>
          {(message ?? lastHarvestAt) && (
            <p className="mt-0.5 text-xs break-words text-gray-500 dark:text-gray-400">
              {lastHarvestAt && `${dateFormat.format(new Date(lastHarvestAt))} · `}
              {message}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={check.isPending}
            onClick={() => {
              check.mutate();
            }}
          >
            {check.isPending && <Loader2 className="animate-spin" aria-hidden />}
            Tester l&apos;entrepôt
          </Button>
          <Button
            type="button"
            disabled={busy || harvest.isPending}
            onClick={() => {
              harvest.mutate(false);
            }}
          >
            <RefreshCw aria-hidden />
            Moissonner maintenant
          </Button>
        </div>
      </div>
      {check.data && (
        <p
          role="status"
          className={
            check.data.success
              ? "text-sm text-emerald-700 dark:text-emerald-400"
              : "text-sm text-red-700 dark:text-red-400"
          }
        >
          {check.data.message}
        </p>
      )}
      {(check.error ?? harvest.error) && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {(check.error ?? harvest.error)?.message}
        </p>
      )}
      {resource.harvest.harvested_until && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Les moissonnages ne reprennent que les titres modifiés depuis le{" "}
          {dateFormat.format(new Date(resource.harvest.harvested_until))}.{" "}
          <button
            type="button"
            disabled={busy || harvest.isPending}
            onClick={() => {
              harvest.mutate(true);
            }}
            className="text-primary font-medium underline-offset-2 hover:underline disabled:opacity-50"
          >
            Tout moissonner à nouveau
          </button>
        </p>
      )}
    </section>
  );
}
